import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ApiService } from './api.service';
import { UserProxy } from '../store/user/user.proxy';
import { firstValueFrom } from 'rxjs';

export type AddonActionType = 'purchased' | 'requested';
export type AddonRequestStatus = 'Created' | 'Pending' | 'Approved' | 'Rejected' | 'Expired' | 'Error';
export type AddonRequestAction = Extract<AddonRequestStatus, 'Approved' | 'Rejected'>;

export interface IAddonPayload {
  featureId: string;
  quantity?: number;
  context?: AddonContext;
}

export interface AddonContext {
  [key: string]: any;
}

export interface AddonRequestItem {
  id: string;
  featureId: string;
  status: AddonRequestStatus;
  requestedBy: string;
  approvedBy?: string;
  createdAt: string;
  expireAt: string;
}

export interface AddonRequestFilters {
  query?: string | null;
  status?: string | null;
  take?: number;
  skip?: number;
}

@Injectable({
  providedIn: 'root',
})
export class AddOnService {
  constructor(private apiService: ApiService, private userProxy: UserProxy) {}

  /**
   * Check if user is Account Admin.
   *
   */
  async isAccountAdmin(): Promise<boolean> {
    return await firstValueFrom(this.userProxy.isAccountAdmin$);
  }

  /**
   * Check if user can see add-on CTAs (HM + Account Admin).
   * - account-admin: can buy
   * - HM: can send a request
   */
  async canSeeAddOn(): Promise<boolean> {
    const [isAccountAdmin, currentRole] = await Promise.all([
      this.isAccountAdmin(),
      firstValueFrom(this.userProxy.currentRole$),
    ]);
    return isAccountAdmin || currentRole === 'hm';
  }

  /**
   * Purchase an add-on.
   * Used by account-admins.
   */
  purchaseAddon(request: IAddonPayload): Observable<any> {
    return this.apiService
      .purchaseAddon(request)
      .pipe(tap(() => this.userProxy.getUser()));
  }

  /**
   * Request an add-on (for non-admin users).
   */
  requestAddon(request: IAddonPayload): Observable<any> {
    return this.apiService.requestAddon(request);
  }

  /**
   * Get addon requests (admin only).
   * Used for managing addon requests in settings.
   */
  getAddonRequests(filters?: AddonRequestFilters): Observable<AddonRequestItem[] | { data: AddonRequestItem[] }> {
    return this.apiService.getAddonRequests(filters);
  }

  /**
   * Approve an addon request (admin only).
   */
  approveAddonRequest(requestId: string): Observable<AddonRequestItem> {
    return this.apiService.approveAddonRequest(requestId);
  }

  /**
   * Reject an addon request (admin only).
   */
  rejectAddonRequest(requestId: string): Observable<AddonRequestItem> {
    return this.apiService.rejectAddonRequest(requestId);
  }
}
